
import { useEffect, useMemo, useState } from "react";
import {
  coverage,
  setLocalReview,
  type ExpertLabel,
  type Review,
} from "./calibrationUtils";
import "./calibration.css";

type Candidate = {
  pair_id: string;
  left_paper_id: string;
  right_paper_id: string;
  left_title: string;
  right_title: string;
  score: number;
  stratum: string;
  components: Record<string, number>;
  shared_directions: string[];
  shared_terms: string[];
};

type Candidates = {
  candidate_count: number;
  status: string;
  production_eligible: boolean;
  review_contract: {
    minimum_reviewed_pairs: number;
    minimum_strong_related: number;
    minimum_weak_related: number;
    minimum_reviewers_per_pair: number;
  };
  candidates: Candidate[];
};

type Status = {
  gate_passed: boolean;
  production_eligible: boolean;
  gold_pair_count: number;
  reason: string;
};

const STORAGE_KEY = "photonbrief-expert-calibration-v1";

function loadReviews(): Review[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]");
  } catch {
    return [];
  }
}

export default function AtlasCalibrationPage() {
  const [data, setData] = useState<Candidates | null>(null);
  const [status, setStatus] = useState<Status | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [reviewer, setReviewer] = useState("");
  const [reviews, setReviews] = useState<Review[]>(loadReviews);

  useEffect(() => {
    Promise.all([
      fetch("/data/atlas/calibration_candidates_v1.json", {
        cache: "no-store",
      }).then((response) => response.json()),
      fetch("/data/atlas/calibration_status_v1.json", {
        cache: "no-store",
      }).then((response) => response.json()),
    ]).then(([candidates, calibrationStatus]) => {
      setData(candidates);
      setStatus(calibrationStatus);
      setSelectedId(candidates.candidates[0]?.pair_id ?? null);
    });
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(reviews));
  }, [reviews]);

  const selected = useMemo(
    () =>
      data?.candidates.find((row) => row.pair_id === selectedId) ??
      null,
    [data, selectedId],
  );
  const progress = coverage(
    data?.candidates.map((row) => row.pair_id) ?? [],
    reviews,
    reviewer.trim(),
  );

  const label = (value: ExpertLabel) => {
    if (!selected || !reviewer.trim()) return;
    setReviews((current) =>
      setLocalReview(current, {
        pair_id: selected.pair_id,
        reviewer_id: reviewer.trim(),
        label: value,
      }),
    );
  };

  const exportLabels = () => {
    const payload = {
      schema_version: "photonbrief-expert-labels-v1",
      exported_at: new Date().toISOString(),
      reviews,
    };
    const blob = new Blob(
      [JSON.stringify(payload, null, 2) + "\n"],
      { type: "application/json" },
    );
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "PhotonBrief_expert_labels_v1.json";
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <main className="cal-main">
      <header className="cal-header">
        <div>
          <small>PB-M09 · EXPERT CALIBRATION WORKBENCH</small>
          <h1>Gold Pair Review</h1>
          <p>
            Human labels only. Similarity scores never label themselves.
          </p>
        </div>
        <div className="cal-gate">
          <b>{status?.gate_passed ? "GATE PASS" : "GATE PENDING"}</b>
          <span>{status?.gold_pair_count ?? 0} consensus gold pairs</span>
          <a href="#/atlas">Back to Atlas</a>
        </div>
      </header>

      <section className="cal-layout">
        <aside className="cal-list">
          <label>
            Reviewer ID
            <input
              value={reviewer}
              onChange={(event) => setReviewer(event.target.value)}
              placeholder="e.g. RL / Expert-A"
            />
          </label>
          <div className="cal-progress">
            <strong>
              {progress.reviewed}/{progress.total}
            </strong>
            <span>Your local review coverage</span>
          </div>
          <div className="cal-pairs">
            {data?.candidates.map((pair) => (
              <button
                key={pair.pair_id}
                className={
                  pair.pair_id === selectedId ? "active" : ""
                }
                onClick={() => setSelectedId(pair.pair_id)}
              >
                <i>{pair.stratum.replaceAll("_", " ")}</i>
                <strong>{Math.round(pair.score * 100)}%</strong>
                <span>{pair.left_title}</span>
                <em>↔ {pair.right_title}</em>
              </button>
            ))}
          </div>
        </aside>

        <section className="cal-center">
          {!selected ? (
            <p>Loading candidate pairs…</p>
          ) : (
            <>
              <div className="cal-pair-title">
                <article>
                  <small>PAPER A</small>
                  <h2>{selected.left_title}</h2>
                  <code>{selected.left_paper_id}</code>
                </article>
                <span>↔</span>
                <article>
                  <small>PAPER B</small>
                  <h2>{selected.right_title}</h2>
                  <code>{selected.right_paper_id}</code>
                </article>
              </div>

              <div className="cal-label-box">
                <h3>Your expert judgment</h3>
                <p>
                  Judge scientific relatedness for domain navigation,
                  independent of the displayed algorithm score.
                </p>
                <div>
                  <button onClick={() => label("STRONG_RELATED")}>
                    Strong related
                  </button>
                  <button onClick={() => label("WEAK_RELATED")}>
                    Weak related
                  </button>
                  <button onClick={() => label("UNCERTAIN")}>
                    Uncertain
                  </button>
                </div>
                {!reviewer.trim() && (
                  <small>Enter Reviewer ID before labeling.</small>
                )}
              </div>

              <section className="cal-context">
                <h3>Algorithm context — do not use as gold label</h3>
                <div className="cal-score">
                  <strong>{Math.round(selected.score * 100)}%</strong>
                  <span>Expert Similarity v1</span>
                </div>
                <dl>
                  {Object.entries(selected.components).map(
                    ([name, value]) => (
                      <div key={name}>
                        <dt>{name.replaceAll("_", " ")}</dt>
                        <dd>{value.toFixed(2)}</dd>
                      </div>
                    ),
                  )}
                </dl>
              </section>
            </>
          )}
        </section>

        <aside className="cal-right">
          <section>
            <h3>Shared expert facets</h3>
            <b>Directions</b>
            <p>
              {selected?.shared_directions.join(" · ") || "None"}
            </p>
            <b>Scientific terms</b>
            <p>{selected?.shared_terms.join(" · ") || "None"}</p>
          </section>
          <section>
            <h3>Gold gate contract</h3>
            <p>
              ≥ {data?.review_contract.minimum_reviewed_pairs ?? 20}
              consensus pairs
            </p>
            <p>
              ≥ {data?.review_contract.minimum_strong_related ?? 5}
              strong
            </p>
            <p>
              ≥ {data?.review_contract.minimum_weak_related ?? 5}
              weak
            </p>
            <p>
              ≥ {data?.review_contract.minimum_reviewers_per_pair ?? 2}
              reviewers per gold pair
            </p>
          </section>
          <section className="cal-warning">
            <h3>Scientific boundary</h3>
            <p>
              Current gate is pending. Local labels are not Canonical
              Science and are not committed automatically.
            </p>
          </section>
          <button onClick={exportLabels}>Export local labels JSON</button>
          <button
            onClick={() => {
              setReviews([]);
              localStorage.removeItem(STORAGE_KEY);
            }}
          >
            Clear local labels
          </button>
        </aside>
      </section>
    </main>
  );
}
